(() => {
  const selectors = {
    section: '[data-bundle-tabs]',
    button: '[data-bundle-tab-button]',
    panel: '[data-bundle-tab-panel]',
    addToCart: '[data-bundle-collection-atc]',
    bundleCard: '[data-bundle-card-save-cents]',
    bundleAutoDesc: '[data-bundle-auto-desc]'
  };

  const tabAliases = {
    bundle: 'bundle',
    bundles: 'bundle',
    'bundle-save': 'bundle',
    'bundle-and-save': 'bundle',
    bundle_save: 'bundle',
    gear: 'select-gear',
    gears: 'select-gear',
    'select-gear': 'select-gear',
    'select-gears': 'select-gear',
    select_gear: 'select-gear',
    select_gears: 'select-gear',
    'limited-gear': 'select-gear',
    'first-order': 'first-order',
    first_order: 'first-order',
    first: 'first-order',
    signup: 'first-order',
    subscribe: 'first-order',
    newsletter: 'first-order',
    discount: 'first-order'
  };

  const normalizeTab = (value) => {
    if (!value) return '';

    const normalized = String(value)
      .trim()
      .replace(/^#/, '')
      .replace(/\s+/g, '-')
      .toLowerCase();

    return tabAliases[normalized] || '';
  };

  const getUrlTab = () => {
    const params = new URLSearchParams(window.location.search);
    const queryTab = normalizeTab(params.get('tab') || params.get('activeTab'));

    if (queryTab) return queryTab;

    return normalizeTab(window.location.hash);
  };

  const updateUrl = (tab) => {
    if (!window.history || !window.history.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);

    if (normalizeTab(url.hash)) {
      url.hash = '';
    }

    window.history.replaceState({ bundleCollectionTab: tab }, '', url.toString());
  };

  const scrollActiveTabIntoView = (button) => {
    if (window.innerWidth > 768 || !button) return;

    const tabs = button.closest('.bundle_collection_tabs_wrap');
    if (!tabs) return;

    const tabsRect = tabs.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const tabsPaddingLeft = parseFloat(window.getComputedStyle(tabs).paddingLeft) || 0;
    const targetLeft = buttonRect.left - tabsRect.left + tabs.scrollLeft - tabsPaddingLeft;
    const maxLeft = Math.max(0, tabs.scrollWidth - tabs.clientWidth);

    tabs.scrollTo({
      left: Math.min(Math.max(targetLeft, 0), maxLeft),
      behavior: 'smooth'
    });
  };

  const setActiveTab = (section, nextTab, shouldUpdateUrl = false) => {
    const buttons = Array.from(section.querySelectorAll(selectors.button));
    const panels = Array.from(section.querySelectorAll(selectors.panel));
    const fallbackTab = buttons[0]?.dataset.bundleTabValue || 'bundle';
    const activeTab = normalizeTab(nextTab) || fallbackTab;
    let activeButton = null;

    buttons.forEach((button) => {
      const isActive = button.dataset.bundleTabValue === activeTab;

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.tabIndex = isActive ? 0 : -1;

      if (isActive) {
        activeButton = button;
      }
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.bundleTabValue === activeTab;

      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    section.dataset.activeTab = activeTab;
    scrollActiveTabIntoView(activeButton);

    if (shouldUpdateUrl) {
      updateUrl(activeTab);
    }
  };

  const focusButtonByIndex = (buttons, index) => {
    const target = buttons[index];

    if (!target) return;

    target.focus();
    setActiveTab(target.closest(selectors.section), target.dataset.bundleTabValue, true);
  };
  
  const updateBundleAutoDescription = (section) => {
    const cards = Array.from(section.querySelectorAll(selectors.bundleCard));
    let maxSaveCents = 0;
    let maxSaveMoney = '';

    cards.forEach((card) => {
      const saveCents = Number(card.dataset.bundleCardSaveCents || 0);

      if (saveCents > maxSaveCents) {
        maxSaveCents = saveCents;
        maxSaveMoney = card.dataset.bundleCardSaveMoney || '';
      }
    });

    if (maxSaveCents <= 0 || !maxSaveMoney) return;

    const autoDescription = `Save Up To ${maxSaveMoney} with exclusive bundle deals`;

    section.querySelectorAll(selectors.bundleAutoDesc).forEach((target) => {
      target.textContent = autoDescription;
    });
  };

  const refreshCartSidebar = () => {
    if (window.Shopify && typeof window.Shopify.getCart === 'function') {
      window.Shopify.getCart((cart) => {
        document.body.classList.add('cart-sidebar-show');

        if (typeof window.updateSidebarCart === 'function') {
          window.updateSidebarCart(cart);
        }
      });
      return;
    }

    const cartIcon = document.querySelector('.header--cart .header__icon--cart, [data-cart-sidebar]');
    cartIcon?.click();
  };

  const addVariantToCart = async (button) => {
    const variantId = button.dataset.variantId;

    if (!variantId || button.classList.contains('is-loading')) return;

    const root = window.Shopify?.routes?.root || window.routes?.root || '/';
    const cartAddUrl = `${root.endsWith('/') ? root : `${root}/`}cart/add.js`;
    const item = { id: variantId, quantity: 1 };
    const preorderValue = button.dataset.preorderInfo;

    if (preorderValue) {
      const formattedPreorderDate = window.extractAndFormatDate(preorderValue);
      item.properties = {
        'Pre-order': formattedPreorderDate
      };
    }

    button.classList.add('is-loading');
    button.disabled = true;

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });

      if (!response.ok) {
        throw new Error('Unable to add item to cart');
      }

      refreshCartSidebar();
    } catch (error) {
      console.error(error);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  };

  const initSection = (section) => {
    if (!section || section.dataset.bundleTabsInitialized === 'true') return;

    const buttons = Array.from(section.querySelectorAll(selectors.button));

    if (buttons.length === 0) return;

    section.dataset.bundleTabsInitialized = 'true';
    updateBundleAutoDescription(section);

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => {
        setActiveTab(section, button.dataset.bundleTabValue, true);
      });

      button.addEventListener('keydown', (event) => {
        const lastIndex = buttons.length - 1;
        let nextIndex = null;

        if (event.key === 'ArrowRight') {
          nextIndex = index === lastIndex ? 0 : index + 1;
        }

        if (event.key === 'ArrowLeft') {
          nextIndex = index === 0 ? lastIndex : index - 1;
        }

        if (event.key === 'Home') {
          nextIndex = 0;
        }

        if (event.key === 'End') {
          nextIndex = lastIndex;
        }

        if (nextIndex !== null) {
          event.preventDefault();
          focusButtonByIndex(buttons, nextIndex);
        }
      });
    });

    section.addEventListener('click', (event) => {
      const addToCartButton = event.target.closest(selectors.addToCart);

      if (!addToCartButton || !section.contains(addToCartButton)) return;

      event.preventDefault();
      addVariantToCart(addToCartButton);
    });

    setActiveTab(section, getUrlTab(), false);
  };

  const initAll = (root = document) => {
    if (root.matches?.(selectors.section)) {
      initSection(root);
    }

    root.querySelectorAll(selectors.section).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  window.addEventListener('popstate', () => {
    document.querySelectorAll(selectors.section).forEach((section) => {
      setActiveTab(section, getUrlTab(), false);
    });
  });
})();
